import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ordersApi } from "../lib/orders-api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

const ADDRESS_FIELDS: { key: string; label: string; span?: boolean }[] = [
  { key: "first_name", label: "Vorname" },
  { key: "last_name", label: "Nachname" },
  { key: "company", label: "Firma", span: true },
  { key: "address1", label: "Straße und Hausnummer", span: true },
  { key: "address2", label: "Adresszusatz", span: true },
  { key: "zip", label: "PLZ" },
  { key: "city", label: "Ort" },
  { key: "province", label: "Bundesland / Region" },
  { key: "country", label: "Land" },
  { key: "country_code", label: "Ländercode (z. B. DE)" },
  { key: "phone", label: "Telefon" },
];

const asText = (v: unknown) => (typeof v === "string" ? v : "");

export function EditAddressDialog({
  open,
  onOpenChange,
  orderId,
  address,
  printerUploaded,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  address: Record<string, unknown> | null;
  /** Das Druckerei-Paket (Order-Upload) ist schon raus. */
  printerUploaded: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(
      Object.fromEntries(ADDRESS_FIELDS.map((f) => [f.key, asText(address?.[f.key])])),
    );
  }, [open, address]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await ordersApi.setShippingAddress(orderId, draft);
      toast.success(r.changed.length ? "Adresse gespeichert" : "Keine Änderung");
      if (r.changed.length && r.already_sent_to_printer)
        toast.warning("Das Paket liegt schon bei der Druckerei – dort steht noch die alte Adresse.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Lieferadresse bearbeiten</DialogTitle>
            <DialogDescription>
              Gilt für den nächsten Druckerei-Upload.
              {printerUploaded &&
                " Das Paket wurde bereits hochgeladen und enthält die alte Adresse."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {ADDRESS_FIELDS.map((f) => (
              <Field key={f.key} className={f.span ? "col-span-2" : undefined}>
                <FieldLabel htmlFor={`addr-${f.key}`}>{f.label}</FieldLabel>
                <Input
                  id={`addr-${f.key}`}
                  value={draft[f.key] ?? ""}
                  maxLength={f.key === "country_code" ? 2 : 120}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                />
              </Field>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Speichere …" : "Speichern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditChildNameDialog({
  open,
  onOpenChange,
  orderId,
  itemId,
  childName,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  itemId: string;
  childName: string;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(childName);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setDraft(childName);
  }, [open, childName]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await ordersApi.setChildName(orderId, itemId, draft);
      if (!r.changed) toast.message("Keine Änderung");
      else
        toast.success(
          r.csv_rebuilt
            ? "Name gespeichert. fields.csv aktualisiert – InDesign neu rendern."
            : "Name gespeichert.",
        );
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Name des Kindes bearbeiten</DialogTitle>
            <DialogDescription>
              Schon gerenderte PDFs und Szenenbilder behalten den alten Namen,
              bis neu gerendert bzw. neu generiert wird.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="edit-child-name">Name</FieldLabel>
            <Input
              id="edit-child-name"
              value={draft}
              maxLength={60}
              onChange={(e) => setDraft(e.target.value)}
              required
              autoFocus
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={busy || !draft.trim()}>
              {busy ? "Speichere …" : "Speichern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
